import React, {useEffect} from 'react';
import cx from 'classnames';
import {
    Flex,
    Icon,
    PriorityIcon,
    Text,
    Input,
    Button
} from '@priority-software/priority-style-react';
import texts from '../../../../../../Localization/AISearch/texts';
import {EmptyResponseMsg} from '../EmptyResponseMsg/EmptyResponseMsg';
import styles from './styles.module.scss';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {AILoader} from '../../../../../../components/AILoader';
import {monitorAction} from '../../../../../../services/datadog';

type Props = {
    handleSearch: (value: string) => void;
    hasError: boolean;
    isLoading: boolean;
    isEmptyResult: boolean;
    showAiSearch: () => void;
    aiSearchVisible: boolean;
    componentClasses?: string;
    monitorInputButton?: string;
};

export const AISearchBlock = (props: Props) => {
    const {
        handleSearch,
        isLoading,
        hasError,
        isEmptyResult,
        showAiSearch,
        aiSearchVisible,
        componentClasses,
        monitorInputButton
    } = props;
    const [value, setValue] = React.useState('');
    const focusRef = React.useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value);

    const onSearch = () => {
        if (!!value) {
            monitorInputButton && monitorAction(monitorInputButton);
            handleSearch(value);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    const renderUXHelpers = () => {
        if (isEmptyResult) {
            return <EmptyResponseMsg showError={hasError} />;
        } else if (isLoading) {
            return (
                <Flex
                    componentClasses={styles.loaderContainer}
                    justifyContent="center"
                    {...createDataTestId('loader-animation', true)}
                >
                    <AILoader />
                </Flex>
            );
        }
        // Show AI search description
        return (
            <Text textColor="secondary-500" skin="label-1">
                {texts.ai_search_description}
            </Text>
        );
    };

    useEffect(() => {
        if (aiSearchVisible && focusRef.current) {
            focusRef.current.focus();
        }
    }, [aiSearchVisible]);

    return (
        <div
            className={cx(styles.component, componentClasses)}
            {...createDataTestId('ai-search-block')}
        >
            <Flex direction="column" alignItems="flex-start" style={{gap: 16}}>
                {!aiSearchVisible ? (
                    <Button
                        skin="aierp"
                        size="medium"
                        onClick={showAiSearch}
                        dataTestId="search-block-ai-btn"
                        children={
                            <Text skin="paragraph-2" componentClasses={styles.aiLabel}>
                                {texts.ai_label}
                            </Text>
                        }
                    />
                ) : (
                    <Input
                        skin="aierp"
                        size="large"
                        autoFocus={true}
                        onKeyDown={handleKeyDown}
                        onChange={handleChange}
                        placeholder={texts.ai_input_placeholder}
                        value={value}
                        children={
                            <Icon
                                icon={PriorityIcon.paperPlane}
                                componentClasses={styles.submitIcon}
                                onClick={onSearch}
                            />
                        }
                    />
                )}

                {renderUXHelpers()}
            </Flex>
        </div>
    );
};
